import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { isSupportedLanguage } from '@/shared/config/languages'
import { useGetUserSession, useSignIn } from '@/shared/hooks/api/auth'
import { useCurrentOrganization } from '@/shared/hooks/api/organization'
import { computeOrganizationAccess } from '@/shared/lib/computeOrganizationAccess'
import {
  ENG_2178_SENTRY_PROBE_QUERY_PARAM,
  maybeReportEng2178ProductionProbe,
} from '@/shared/lib/eng2178SentryProbe'
import { shouldShowViewOnlyAccessNotReady } from '@/shared/lib/readOnlyAccess'
import i18n from 'i18next'
import { Loader2 } from 'lucide-react'
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router'
import { Toaster } from '@/components/ui/sonner'
import { SignUp } from '@/features/auth/sign-up'
import { OnboardingWizard } from '@/features/onboarding/onboarding-wizard'
import App from './App'
import { SignIn } from './SignIn'
import { ChangePassword } from './ChangePassword'
import { TrialAccessBlocked } from './TrialAccessBlocked'
import { ViewOnlyAccessNotReady } from './ViewOnlyAccessNotReady'

/** Paths where an unauthenticated user stays on the URL (matches main.tsx isAuthPath). */
const PUBLIC_UNAUTH_PATH_PREFIXES = [
  '/sign-in',
  '/signup',
  '/sign-up',
] as const

function isPublicUnauthenticatedPath(pathname: string): boolean {
  return PUBLIC_UNAUTH_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

function safeRedirectPath(redirect: string | null): string {
  if (!redirect) {
    return '/'
  }
  if (redirect.startsWith('http://') || redirect.startsWith('https://')) {
    return '/'
  }
  if (!redirect.startsWith('/') || redirect.startsWith('//')) {
    return '/'
  }
  return redirect
}

/** After sign-in, `/` is already the default; omit `?redirect=` for a cleaner URL. */
function signInPathForReturnTo(returnTo: string): string {
  if (returnTo === '/') {
    return '/sign-in'
  }
  return `/sign-in?redirect=${encodeURIComponent(returnTo)}`
}

function LoadingScreen() {
  return (
    <div className='flex min-h-svh items-center justify-center'>
      <Loader2 className='size-8 animate-spin text-muted-foreground' />
    </div>
  )
}

export default function Root() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const postSignInPath = useMemo(
    () => safeRedirectPath(searchParams.get('redirect')),
    [searchParams]
  )
  const {
    data: session,
    refetch: getSession,
    isFetched: isAuthCheckComplete,
  } = useGetUserSession()
  const organizationQuery = useCurrentOrganization()
  const { mutateAsync: signIn, isPending: isSigningIn } = useSignIn()
  const isAuthenticated = !!session

  useEffect(() => {
    if (!session) {
      return
    }

    maybeReportEng2178ProductionProbe({
      isArtemAdmin: session.isArtemAdmin,
      probeValue: searchParams.get(ENG_2178_SENTRY_PROBE_QUERY_PARAM),
    })
  }, [searchParams, session])

  useLayoutEffect(() => {
    const sessionLang = session?.lang
    if (sessionLang != null && isSupportedLanguage(sessionLang)) {
      void i18n.changeLanguage(sessionLang)
    }
  }, [session?.lang])

  const handleSignIn = useCallback(
    async (payload: { email: string; password: string }) => {
      await signIn(payload)
      const sessionResult = await getSession()
      if (sessionResult.data?.mustChangePassword) {
        navigate('/change-password', { replace: true })
        return
      }
      navigate(postSignInPath, { replace: true })
    },
    [signIn, getSession, navigate, postSignInPath]
  )

  if (!isAuthCheckComplete) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    if (location.pathname === '/sign-up') {
      return <Navigate to="/signup" replace />
    }
    if (location.pathname === '/signup') {
      return (
        <>
          <SignUp />
          <Toaster duration={5000} />
        </>
      )
    }
    if (!isPublicUnauthenticatedPath(location.pathname)) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`
      return <Navigate to={signInPathForReturnTo(returnTo)} replace />
    }
    return (
      <>
        <SignIn onSignIn={handleSignIn} isSigningIn={isSigningIn} />
        <Toaster duration={5000} />
      </>
    )
  }

  if (session.mustChangePassword) {
    if (location.pathname !== '/change-password') {
      return <Navigate to='/change-password' replace />
    }

    return (
      <>
        <ChangePassword />
        <Toaster duration={5000} />
      </>
    )
  }

  if (location.pathname === '/change-password') {
    return <Navigate to='/' replace />
  }

  if (organizationQuery.isLoading) {
    return <LoadingScreen />
  }

  if (organizationQuery.isError || !organizationQuery.data) {
    return <LoadingScreen />
  }

  const organization = organizationQuery.data

  if (session.isArtemAdmin && location.pathname === '/onboarding') {
    return <Navigate to='/' replace />
  }

  if (
    shouldShowViewOnlyAccessNotReady({
      isReadOnly: session.isReadOnly,
      onboardingCompletedAt: organization.onboardingCompletedAt,
    })
  ) {
    return (
      <>
        <ViewOnlyAccessNotReady />
        <Toaster duration={5000} />
      </>
    )
  }

  if (!session.isArtemAdmin && organization.onboardingCompletedAt == null) {
    if (location.pathname !== '/onboarding') {
      return <Navigate to='/onboarding' replace />
    }
    return (
      <>
        <OnboardingWizard />
        <Toaster duration={5000} />
      </>
    )
  }

  const organizationAccess = computeOrganizationAccess({
    organization: {
      trialTermsAcceptedAt: organization.trialTermsAcceptedAt,
      trialEndsAt: organization.trialEndsAt,
      accessPlan: organization.accessPlan,
    },
    isArtemAdmin: session.isArtemAdmin,
    now: new Date(),
  })

  if (!organizationAccess.canUseApp) {
    return (
      <>
        <TrialAccessBlocked />
        <Toaster duration={5000} />
      </>
    )
  }

  return (
    <>
      <App />
      <Toaster duration={5000} />
    </>
  )
}
